import { useCallback, useRef } from 'react';
import { getMyClients } from '../../../api/hub';
import { getWorkList } from '../../../api/hub/work';

/** The tasks of a `fn_work_list` payload as `{id, title}`, grouped buckets flattened. */
const flattenTasks = (payload) => {
  const seen = new Set();
  const tasks = [];
  for (const group of Array.isArray(payload?.groups) ? payload.groups : []) {
    for (const item of Array.isArray(group?.items) ? group.items : []) {
      if (!item?.id || seen.has(item.id)) continue;
      seen.add(item.id);
      tasks.push({ id: item.id, title: item.title || 'Untitled work' });
    }
  }
  return tasks;
};

/**
 * The project picker's data: the caller's client roster and one client's open tasks, each
 * fetched on first use and cached for the page. A failed fetch is not cached, so
 * reopening the picker retries.
 *
 * The task list is the first page of `fn_work_list` (200 items, subtasks and finished work
 * excluded); the picker filters it as you type, so a client with more open tasks than that
 * is searched only among the first 200.
 *
 * @returns {{
 *   loadClients: () => Promise<{id: string, name: string}[]>,
 *   loadTasks: (clientId: string) => Promise<{id: string, title: string}[]>,
 * }}
 */
export default function useProjectOptions() {
  const clients = useRef(null);
  const tasks = useRef(new Map());

  const loadClients = useCallback(() => {
    if (!clients.current) {
      const request = getMyClients().then((r) => (Array.isArray(r?.data) ? r.data : []));
      clients.current = request;
      request.catch(() => {
        if (clients.current === request) clients.current = null;
      });
    }
    return clients.current;
  }, []);

  const loadTasks = useCallback((clientId) => {
    let request = tasks.current.get(clientId);
    if (!request) {
      request = getWorkList({ client_id: clientId, include_done: false, limit: 200 })
        .then((r) => flattenTasks(r?.data));
      tasks.current.set(clientId, request);
      request.catch(() => {
        if (tasks.current.get(clientId) === request) tasks.current.delete(clientId);
      });
    }
    return request;
  }, []);

  return { loadClients, loadTasks };
}
